import {
  teamTasks, doneToday, watchTeamTasks, createTask, setTaskState, updateTask,
  assignTask, blockTask, unblockTask, taskActivity, addTaskActivity,
  allProjects, allUsers, bankTaskTime, getTask, listLeaveRequests,
} from '../db.js';
import { dayKey, teamFrom, meIn } from '../tasks.js';
import { parseAdd, hm, backFromLeave } from '../tasks-v2.js';
import { headHtml, headSub, mineHtml, teamHtml, viewFrom, addHint } from './tasks-page.js';
import { openStandup } from './tasks-standup.js';
import { openTaskPanel } from './task-panel.js';
import { createTaskActions } from './task-actions.js';
import { blockerFrom } from './task-blocker.js';
import { openNewTask, openEditTask } from './task-new.js';
import { putInDiary } from './task-diary.js';
import { goToView } from './calc-icon.js';
import * as timer from '../task-timer.js';

// THE TASKS PAGE, v2 (design_handoff_tasks_v2, 18/10/2026).
//
// Two views. MINE (default) answers three questions in order: what is waiting
// on you, what is on your list, what are you waiting on. TEAM is the 09:15
// table, one row per person, Unassigned last. A brief row over each says the
// same thing in a sentence that names the records. Board, Projects, the digest,
// Ask Track's drawer, the load bars and the timer are gone (the hand-off's
// "Removed" table) — the project record has its own board.
//
// ONE READ. Everyone sees everyone's work, so the page takes a single read of
// the open tasks (plus today's done, so a ticked row stays struck through) and
// both views group it in the browser through tasks-v2.js.

export async function renderTasks(view, session) {
  // OWN ELEMENT, NOT #view. #view is the same element on every screen; the old
  // page hung its listeners and its live snapshot straight on it, nothing
  // removed them, and a task moving anywhere repainted Tasks over whatever
  // screen you were on. A child element dies with the route, and its listeners
  // with it. The panel is the one exception: it goes on <body>, because the
  // view column is a containing block that would pin a position:fixed sheet
  // inside it — and the teardown below closes it when the route changes.
  const mount = document.createElement('div');
  mount.className = 'tk2-root';
  const page = document.createElement('div');
  page.className = 'tk2-page';
  mount.appendChild(page);
  view.replaceChildren(mount);

  const st = {
    view: viewFrom(sessionStorage.getItem('tk-view')),
    open: [], done: [], people: [], projects: [], byId: {},
    expanded: new Set(), acted: {}, flash: null, flashKind: 'error', updated: '', panel: null, loaded: false,
  };

  const [rawProjects, rawUsers, leave] = await Promise.all([
    allProjects().catch(() => []), allUsers().catch(() => []), listLeaveRequests().catch(() => []),
  ]);
  st.byId = Object.fromEntries(rawProjects.map((p) => [p.id, p]));
  st.rawProjects = rawProjects;
  // The New task form's picker reads number, client and site.
  st.projects = rawProjects.map((p) => ({
    id: p.id, name: p.clientName || p.title || '', number: p.number || '',
    site: (p.siteAddress && (p.siteAddress.line1 || p.siteAddress.postcode)) || p.title || '',
    status: p.status || '', lead: p.lead || null,
  })).filter((p) => p.name || p.number);
  st.people = teamFrom(rawUsers, leave, dayKey());
  // "On leave until Mon 26/10" on Team: the day each person on leave is back.
  st.backOn = Object.fromEntries(st.people.map((p) => [p.userId, backFromLeave(leave, p.email, dayKey())]).filter(([, d]) => d));
  // The add line's example uses a real project number, never a made-up one.
  st.example = String((rawProjects.filter((p) => /^\d{4}$/.test(String(p.number || ''))).sort((a, b) => String(b.number).localeCompare(String(a.number)))[0] || {}).number || '');
  const me = meIn(st.people, session?.email);
  const meId = me ? me.userId : null;

  const all = () => {
    const ids = new Set(st.open.map((t) => t.id));
    return st.open.concat(st.done.filter((t) => !ids.has(t.id)));
  };
  const find = (id) => all().find((t) => t.id === id) || null;
  const stampNow = () => new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  async function load() {
    const [open, done] = await Promise.all([
      teamTasks().catch((e) => { flash(`Tasks did not load — ${(e && e.message) || e}`); return null; }),
      doneToday(dayKey()).catch(() => []),
    ]);
    if (open) st.open = open;
    st.done = done || [];
    st.updated = stampNow();
    st.loaded = true;
    paint();
  }

  // A WRITE THAT FAILS SAYS SO. The old page ended writes in .catch(() => {}),
  // so a refused tick looked exactly like a saved one until the next reload.
  function flash(text, kind = 'error') { st.flash = text; st.flashKind = kind; paint(); }
  const fail = (what) => (e) => {
    console.error('[tasks]', what, e);
    flash(`${what} did not save — ${(e && e.message) || e}`);
    load();
  };

  /** Apply a change on screen before the write lands (ticks, chases). */
  function optimistic(id, patch) {
    const t = find(id);
    if (!t) return null;
    Object.assign(t, patch);
    if (patch.state === 'done') {
      st.open = st.open.filter((x) => x.id !== id);
      if (!st.done.some((x) => x.id === id)) st.done.push(t);
    } else if (patch.state && !st.open.some((x) => x.id === id)) {
      st.done = st.done.filter((x) => x.id !== id);
      st.open.push(t);
    }
    paint();
    return t;
  }

  // ── Paint ─────────────────────────────────────────────────────────────────
  function paint() {
    if (!document.contains(mount)) return;
    // The add line survives a repaint (the live listener fires while you type).
    const add = page.querySelector('[data-add]');
    const keep = add && document.activeElement === add ? { v: add.value, s: add.selectionStart } : null;
    const draft = add ? add.value : '';

    const today = dayKey();
    const tasks = all();
    const vm = { tasks, people: st.people, projects: st.byId, meId, today, updated: st.updated,
      acted: st.acted, expanded: st.expanded, openId: st.panel ? st.panel.taskId : null,
      backOn: st.backOn, example: st.example };
    page.innerHTML = headHtml({ view: st.view, sub: st.loaded ? headSub(st.view, vm) : '' })
      + (st.flash ? `<p class="tk2-flash${st.flashKind === 'ok' ? ' tk2-flash-ok' : ''}" role="${st.flashKind === 'ok' ? 'status' : 'alert'}"></p>` : '')
      + (!st.loaded ? '<p class="tk4-empty">Loading…</p>'
        : st.view === 'team' ? teamHtml(vm) : mineHtml(vm));
    if (st.flash) {
      const p = page.querySelector('.tk2-flash');
      p.textContent = st.flash;
      const x = document.createElement('button');
      x.type = 'button'; x.className = 'tk2-flash-x'; x.setAttribute('aria-label', 'Dismiss'); x.textContent = '×';
      x.addEventListener('click', () => { st.flash = null; paint(); });
      p.appendChild(x);
    }
    const nextAdd = page.querySelector('[data-add]');
    if (nextAdd && draft) { nextAdd.value = draft; hint(nextAdd); }
    if (nextAdd && keep) { nextAdd.focus(); try { nextAdd.setSelectionRange(keep.s, keep.s); } catch (_) { /* */ } }
    if (st.panel) st.panel.refresh(find(st.panel.taskId), { tasks, project: projectOf(find(st.panel.taskId)) });
  }
  const projectOf = (t) => (t && st.byId[t.projectId]) || null;

  function hint(input) {
    const h = page.querySelector('.tk4-add-hint');
    if (h) h.textContent = input.value.trim() ? addHint(parseAdd(input.value, st.rawProjects)) : '';
  }

  // ── Writes (views/task-actions.js, shared with the project tab) ─────────────
  const nameOf = (id) => (st.people.find((p) => p.userId === id) || {}).name || '';
  const { tick, onAction, chaseOrNudge } = createTaskActions({
    session, find, people: () => st.people, optimistic, fail, acted: st.acted,
  });

  function openPanel(id, mode) {
    const t = find(id);
    if (!t) return;
    if (st.panel) st.panel.close();
    st.panel = openTaskPanel({
      host: document.body, task: t, people: st.people, project: projectOf(t), tasks: all(), meId, mode,
      loadActivity: () => taskActivity(id),
      onComment: (text) => addTaskActivity(id, { kind: 'comment', text }, session),
      onAction: (key, payload) => onAction(id, key, payload),
      onEdit: (task) => openEditTask({
        task: { ...task, projectRef: (projectOf(task) && projectOf(task).number) || '' }, projects: st.projects, people: st.people,
        onSave: async (patch) => {
          optimistic(task.id, patch);
          await updateTask(task.id, patch, session).catch(fail('The edit'));
        },
      }),
      onDiary: (task) => putInDiary({
        task, project: projectOf(task),
        onSaved: async (patch) => { optimistic(task.id, patch); await updateTask(task.id, patch, session).catch(fail('The diary entry')); },
      }),
      onLogTime: (t) => document.dispatchEvent(new CustomEvent('track:log-time', { detail: { projectId: (t && t.projectId) || null } })),
      onClose: () => { st.panel = null; paint(); },
    });
    paint();
  }

  function newTask() {
    openNewTask({
      projects: st.projects, people: st.people, today: dayKey(), me,
      onCreate: async (fields) => { await createTask(fields, session); return load(); },
    });
  }

  async function saveAdd(input) {
    const parsed = parseAdd(input.value, st.rawProjects);
    if (!parsed) return;
    if (!me) { flash('You are not on the team list, so a task cannot be given to you here. Use + to add one for someone.'); return; }
    input.value = '';
    hint(input);
    try {
      await createTask({
        title: parsed.title, projectId: parsed.project ? parsed.project.id : null,
        assigneeId: me.userId, assigneeEmail: me.email,
      }, session);
    } catch (e) {
      input.value = parsed.title;
      return fail('The task')(e);
    }
    load();
  }

  // STANDUP MODE (p09): the team full screen, one person at a time, adding
  // tasks for whoever is talking. Reads the same lists; writes only createTask.
  function standup() {
    openStandup({
      people: st.people, meId, projects: st.byId, rawProjects: st.rawProjects, backOn: st.backOn,
      tasks: () => all(),
      onCreate: async (fields) => { await createTask(fields, session); await load(); },
    });
  }

  function setView(v) {
    st.view = v;
    sessionStorage.setItem('tk-view', v);
    paint();
  }

  // ── Events ────────────────────────────────────────────────────────────────
  page.addEventListener('click', (ev) => {
    const tab = ev.target.closest('[data-tab]');
    if (tab) return setView(tab.dataset.tab);
    if (ev.target.closest('[data-new]')) return newTask();
    if (ev.target.closest('[data-standup]')) return standup();
    const tk = ev.target.closest('[data-tick]');
    if (tk) { ev.stopPropagation(); return tick(tk.dataset.tick); }
    const ch = ev.target.closest('[data-chase]');
    if (ch) return chaseOrNudge(ch.dataset.chase, 'chase').catch(() => {});
    const nu = ev.target.closest('[data-nudge]');
    if (nu) return chaseOrNudge(nu.dataset.nudge, 'nudge').catch(() => {});
    const as = ev.target.closest('[data-assign]');
    if (as) return openPanel(as.dataset.assign, 'assign');
    const op = ev.target.closest('[data-open]');
    if (op) { ev.preventDefault(); return openPanel(op.dataset.open); }
    const person = ev.target.closest('[data-person]');
    if (person) return toggle(person.dataset.person);
    if (ev.target.closest('button, a, input')) return;
    const row = ev.target.closest('.tk4-row[data-id]');
    if (row) return openPanel(row.dataset.id);
  });
  function toggle(key) {
    if (st.expanded.has(key)) st.expanded.delete(key); else st.expanded.add(key);
    paint();
  }
  page.addEventListener('keydown', (ev) => {
    const add = ev.target.closest('[data-add]');
    if (add) {
      if (ev.key === 'Enter') { ev.preventDefault(); saveAdd(add); }
      if (ev.key === 'Escape') { ev.preventDefault(); add.value = ''; hint(add); add.blur(); }
      return;
    }
    const person = ev.target.closest('[data-person]');
    if (person && (ev.key === 'Enter' || ev.key === ' ')) { ev.preventDefault(); toggle(person.dataset.person); }
  });
  page.addEventListener('input', (ev) => {
    const add = ev.target.closest('[data-add]');
    if (add) hint(add);
  });

  // N opens the add line (the old ⌘K hint was wrong; the key is N).
  const onKey = (ev) => {
    if (!document.contains(mount)) return;
    const t = ev.target;
    if (t && (/input|textarea|select/i.test(t.tagName) || t.isContentEditable)) return;
    if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
    if (document.querySelector('.tf-scrim, .askbar-panel')) return;
    if (ev.key === 'n' || ev.key === 'N') {
      ev.preventDefault();
      if (st.view !== 'mine') setView('mine');
      page.querySelector('[data-add]')?.focus();
    }
  };
  document.addEventListener('keydown', onKey);
  const onAdded = () => { if (document.contains(mount)) load(); };
  document.addEventListener('track:task-added', onAdded);

  paint();
  await load();
  await bankRunningTimer();

  // Live: the page re-renders when anyone moves anything, which is what makes
  // the 09:15 meeting readable off one screen.
  const stop = watchTeamTasks((rows) => {
    if (!rows || !document.contains(mount)) return;
    st.open = rows;
    paint();
  });
  const teardown = () => {
    document.removeEventListener('keydown', onKey);
    document.removeEventListener('track:task-added', onAdded);
    if (st.panel) st.panel.close();
    if (stop) stop();
  };
  const observer = new MutationObserver(() => {
    if (!document.contains(mount)) { teardown(); observer.disconnect(); }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  /**
   * BANKED TIME (hand-off: "make sure no running timer is lost"). The task
   * timer went with v2. A timer someone left running before the release is
   * still in localStorage; stop it, book its time exactly as the old page did
   * (bankTaskTime, the task fetched by id if it is not in the list), and say so.
   */
  async function bankRunningTimer() {
    const r = timer.running();
    if (!r) return;
    const stopped = timer.stop();
    if (!stopped || !stopped.seconds) return;
    const time = hm(Math.round(stopped.seconds / 60));
    let t = find(stopped.taskId);
    if (!t) t = await getTask(stopped.taskId).catch(() => null);
    if (!t) {
      console.error('[tasks] could not find task %s to bank %ss — the time is NOT booked', stopped.taskId, stopped.seconds);
      flash(`Your running timer was stopped, but its task could not be found, so ${time} was not booked. Add it on the timesheet.`);
      return;
    }
    const p = st.byId[t.projectId];
    try {
      const res = await bankTaskTime(session, {
        taskId: t.id, seconds: stopped.seconds,
        projectId: t.projectId, number: p?.number || '',
        label: p?.clientName || p?.title || '', task: p?.service || '',
      });
      if (res && res.error) {
        console.error('[tasks] time not fully booked for %s:', t.id, res.error);
        flash(`Your running timer on “${t.title}” was stopped. ${time} is kept on the task, but the timesheet refused it — ${res.error}`);
        return;
      }
      flash(`Your running timer on “${t.title}” was stopped and ${time} booked to the timesheet.`, 'ok');
    } catch (e) {
      console.error('[tasks] banking the stopped timer failed', e);
      flash(`Your running timer on “${t.title}” was stopped, but ${time} did not book — ${(e && e.message) || e}. Add it on the timesheet.`);
    }
  }
}
